/**
 * Join codes.
 *
 * A join code is the only thing standing between a student in an orientation
 * session and an institution's dashboard. It gets read off a poster on a wall,
 * off a slide at the back of a lecture theatre, and out of an advisor's email,
 * then typed into a phone. Every design choice here is about that reading.
 *
 * ── The alphabet, and why it excludes BOTH sides of every confusion pair ────
 * The usual trick (Crockford base32) keeps one member of each confusable pair
 * and folds the other onto it: O becomes 0, I and L become 1. That works when
 * the survivor is in the alphabet. Here it is not, because a join code is read
 * at distance and at an angle, and 0 next to O in the same alphabet is a
 * support ticket whichever one you keep.
 *
 * So both sides go:
 *
 *   O 0   I 1 L   S 5   B 8
 *
 * The consequence is worth stating, because it is the whole point: a misread
 * character can never silently resolve to a DIFFERENT VALID CODE. It resolves
 * to nothing, and the student is told which character is wrong, which is a
 * recoverable state. A fold would have quietly sent them to another
 * institution's join page, which is not.
 *
 * 27 characters, 6 positions, 387,420,489 codes. P3 will not exhaust that.
 *
 * ── Normalisation is deliberately generous ─────────────────────────────────
 * Students type lowercase. They add the hyphen they saw on the poster. They
 * copy the whole link out of the email and paste it into the code box. All
 * three are the same intent and all three are accepted. The strictness lives
 * in the alphabet, not in the parser.
 */

/**
 * The 27 characters a code can contain. Uppercase, and in this order, so a
 * generated code's distribution is uniform over exactly this set.
 */
export const JOIN_CODE_ALPHABET = "ACDEFGHJKMNPQRTUVWXYZ234679";

/** Six. Long enough to be unguessable at P3's scale, short enough to say out loud. */
export const JOIN_CODE_LENGTH = 6;

/**
 * The characters that are never in a code, in confusion-pair order. Rendered
 * to the student when they type one, because "we never use the letter O"
 * explains the failure and "invalid code" does not.
 */
export const JOIN_CODE_EXCLUDED = "O0I1LS5B8";

/** Where a join link points. The host is the one on the poster and in the email. */
/* The join host (Sep 9 2026): a second Custom Domain on the enterprise Worker,
   so /join/<CODE> is the same app there. www.pulseofp3.org is Webflow and
   answered /join/* with a 404. */
export const JOIN_ORIGIN = "https://join.pulseofp3.org";

/**
 * How a student arrived. Stored on the claim event so an administrator can see
 * which artifact worked, never on the student.
 */
export type ClaimSource =
  | "link"
  | "qr"
  | "advisor"
  | "code"
  | "domain-new"
  | "domain-existing";

/**
 * The three sources that travel in a URL, and the short token each one uses.
 *
 * The token is short because it is printed: `?s=adv` on the end of a link in an
 * advisor's email is invisible, `?s=advisor` starts to look like tracking to a
 * reader who is already suspicious of a link about their school. The long form
 * is accepted on the way in so a hand-edited link still works.
 */
const URL_SOURCE_TOKENS: Readonly<Record<"link" | "qr" | "advisor", string>> = {
  link: "link",
  qr: "qr",
  advisor: "adv",
};

const URL_SOURCE_BY_TOKEN: ReadonlyMap<string, ClaimSource> = new Map([
  ["link", "link" as ClaimSource],
  ["qr", "qr" as ClaimSource],
  ["adv", "advisor" as ClaimSource],
  ["advisor", "advisor" as ClaimSource],
]);

export type NormalizeFailure =
  /** Nothing was typed, or the input held no code-shaped characters at all. */
  | "empty"
  /** Fewer than six usable characters. */
  | "too-short"
  /** More than six usable characters. */
  | "too-long"
  /** Six characters, but at least one is not in the alphabet. */
  | "bad-characters";

export type NormalizeResult =
  | { readonly ok: true; readonly code: string }
  | {
      readonly ok: false;
      readonly reason: NormalizeFailure;
      /** What survived cleaning, so a field can echo it back and be corrected. */
      readonly cleaned: string;
      /** The offending characters, de-duplicated, in the order they appeared. */
      readonly badCharacters: readonly string[];
    };

const ALPHABET_SET: ReadonlySet<string> = new Set(JOIN_CODE_ALPHABET.split(""));

/**
 * Pulls the code-shaped part out of whatever the student actually gave us.
 *
 * Handles, in order: a pasted join URL (take the segment after `/join/`, or the
 * last path segment), a query string (drop it), and every separator anyone puts
 * in a six-character code (spaces, hyphens, en and em dashes, underscores,
 * dots, non-breaking spaces).
 */
function extractCandidate(input: string): string {
  let raw = input.trim();
  if (raw === "") return "";

  // A pasted link. Also catches "pulseofp3.org/join/ABC123" with no scheme.
  if (raw.includes("/")) {
    const withoutQuery = raw.split(/[?#]/, 1)[0] ?? "";
    const segments = withoutQuery.split("/").filter((s) => s !== "");
    const joinAt = segments.findIndex((s) => s.toLowerCase() === "join");
    const picked =
      joinAt >= 0 ? segments[joinAt + 1] : segments[segments.length - 1];
    raw = picked ?? "";
  } else {
    raw = raw.split(/[?#]/, 1)[0] ?? "";
  }

  // Everything that is not a letter or a digit is a separator, not a character.
  return raw.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
}

/**
 * Turns anything a student can plausibly type into a canonical code, or into a
 * failure specific enough to write a sentence about.
 */
export function normalizeJoinCode(input: string): NormalizeResult {
  const cleaned = extractCandidate(input ?? "");

  if (cleaned === "") {
    return { ok: false, reason: "empty", cleaned, badCharacters: [] };
  }

  const bad: string[] = [];
  for (const ch of cleaned) {
    if (!ALPHABET_SET.has(ch) && !bad.includes(ch)) bad.push(ch);
  }

  // Length is reported first only when the characters are all legal. A student
  // who typed five good characters is mid-entry; one who typed an O has a
  // different problem and needs to hear about the O.
  if (bad.length > 0) {
    return { ok: false, reason: "bad-characters", cleaned, badCharacters: bad };
  }
  if (cleaned.length < JOIN_CODE_LENGTH) {
    return { ok: false, reason: "too-short", cleaned, badCharacters: [] };
  }
  if (cleaned.length > JOIN_CODE_LENGTH) {
    return { ok: false, reason: "too-long", cleaned, badCharacters: [] };
  }

  return { ok: true, code: cleaned };
}

/** True only for an already-canonical code. Does not normalise first. */
export function isJoinCode(value: string): boolean {
  if (value.length !== JOIN_CODE_LENGTH) return false;
  for (const ch of value) if (!ALPHABET_SET.has(ch)) return false;
  return true;
}

/**
 * The characters, for a six-box entry field or for the poster's large type.
 * Returns an empty array rather than throwing: this is a rendering helper and a
 * half-typed code is a normal state, not an error.
 */
export function joinCodeCharacters(code: string): readonly string[] {
  return code.split("");
}

/**
 * The code as it is spoken and printed: two groups of three. Easier to hold in
 * working memory across the walk from the poster to the phone.
 */
export function groupJoinCode(code: string): string {
  if (code.length !== JOIN_CODE_LENGTH) return code;
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

/**
 * The message a student sees when a code will not parse. Written for the person
 * standing in an orientation session, not for a log.
 */
export function describeNormalizeFailure(result: NormalizeResult): string {
  if (result.ok) return "";
  switch (result.reason) {
    case "empty":
      return "Enter the six-character code from your school.";
    case "too-short":
      return `A join code is ${JOIN_CODE_LENGTH} characters. You have ${result.cleaned.length} so far.`;
    case "too-long":
      return `A join code is ${JOIN_CODE_LENGTH} characters. You have entered ${result.cleaned.length}.`;
    case "bad-characters": {
      const list = result.badCharacters.join(", ");
      return `Join codes never use ${list}. Check it against the code your school gave you: we leave out the letter O and the number 0, the letter I and the number 1, the letter L, S and 5, and B and 8, because they look alike.`;
    }
  }
}

/* ==========================================================================
   Generation
   ========================================================================== */

/** Returns a float in [0, 1). Injectable so tests are deterministic. */
export type RandomFn = () => number;

/**
 * A uniform index into the alphabet.
 *
 * 27 does not divide 256, so taking a byte modulo 27 makes the first four
 * characters of the alphabet ~4% more likely than the rest. At six positions
 * that is not a security problem, but it is a measurable bias in a value P3
 * will print a few hundred times, so it is sampled properly: bytes at or above
 * 243 (9 x 27) are rejected and redrawn.
 */
function randomIndex(random: RandomFn | undefined): number {
  const n = JOIN_CODE_ALPHABET.length;
  if (random) return Math.floor(random() * n) % n;

  const webcrypto = globalThis.crypto;
  if (webcrypto && typeof webcrypto.getRandomValues === "function") {
    const limit = Math.floor(256 / n) * n; // 243
    const buf = new Uint8Array(1);
    // Bounded: each draw rejects with probability 13/256, so this terminates.
    for (let attempt = 0; attempt < 64; attempt++) {
      webcrypto.getRandomValues(buf);
      const byte = buf[0] ?? 0;
      if (byte < limit) return byte % n;
    }
  }
  return Math.floor(Math.random() * n) % n;
}

function randomCharacter(random: RandomFn | undefined): string {
  return JOIN_CODE_ALPHABET.charAt(randomIndex(random));
}

/** A random six-character code. Always valid by construction. */
export function generateJoinCode(options?: { random?: RandomFn }): string {
  let code = "";
  for (let i = 0; i < JOIN_CODE_LENGTH; i++) {
    code += randomCharacter(options?.random);
  }
  return code;
}

/**
 * Words that carry no identity and so are never the memorable part of a code.
 * "University of Houston" should suggest HUSTNP, not UNVERS.
 */
const STOP_WORDS: ReadonlySet<string> = new Set([
  "the",
  "of",
  "at",
  "and",
  "for",
  "university",
  "universities",
  "college",
  "colleges",
  "school",
  "schools",
  "institute",
  "institution",
  "academy",
  "district",
  "campus",
  "state",
  "community",
  "public",
  "system",
  "inc",
  "incorporated",
]);

/**
 * Suggests a code from an institution's name, so the thing on the poster looks
 * deliberate rather than machine-issued.
 *
 * Note that the characters excluded from the alphabet are dropped from the name
 * too, so "Howard University" suggests HWARDU, not HOWARD. That is the cost of
 * an alphabet a student can read off a wall, and it is the right trade: a code
 * only has to be recognisable next to the institution's name, which is printed
 * directly above it on every artifact that carries it.
 *
 * Uniqueness is not this function's job. The caller checks the suggestion
 * against issued codes and calls `generateJoinCode` if it is taken.
 */
export function deriveJoinCode(
  institutionName: string,
  options?: { random?: RandomFn },
): string {
  const words = (institutionName ?? "")
    .split(/[^0-9A-Za-z]+/)
    .filter((w) => w !== "");

  // Identity-carrying words first, then the generic ones, so a short name still
  // fills six characters without repeating itself: "Howard University" walks
  // HOWARD (losing the O) and then reaches into UNIVERSITY for the sixth.
  const meaningful = words.filter((w) => !STOP_WORDS.has(w.toLowerCase()));
  const generic = words.filter((w) => STOP_WORDS.has(w.toLowerCase()));
  const ordered = meaningful.length > 0 ? [...meaningful, ...generic] : words;

  let code = "";
  for (const word of ordered) {
    for (const ch of word.toUpperCase()) {
      if (ALPHABET_SET.has(ch)) code += ch;
      if (code.length === JOIN_CODE_LENGTH) return code;
    }
  }

  while (code.length < JOIN_CODE_LENGTH) {
    code += randomCharacter(options?.random);
  }
  return code;
}

/* ==========================================================================
   Links
   ========================================================================== */

/**
 * The URL on the poster, in the email, and behind the QR.
 *
 * The source marker is a property of the ARTIFACT, not of the student. It is
 * recorded on the claim event so an administrator can see that the poster
 * outperformed the email. It never identifies anybody.
 */
export function joinUrl(
  code: string,
  source?: Extract<ClaimSource, "link" | "qr" | "advisor">,
): string {
  const base = `${JOIN_ORIGIN}/join/${code}`;
  return source ? `${base}?s=${URL_SOURCE_TOKENS[source]}` : base;
}

/**
 * Pulls the code out of `/join/:code`, normalising it, so a link typed by hand
 * in lowercase still lands. Returns null when the path is not a join path or
 * the code will not parse.
 */
export function parseJoinPath(pathname: string): string | null {
  const segments = (pathname ?? "").split("/").filter((s) => s !== "");
  const joinAt = segments.findIndex((s) => s.toLowerCase() === "join");
  if (joinAt < 0) return null;
  const raw = segments[joinAt + 1];
  if (raw === undefined) return null;
  const result = normalizeJoinCode(raw);
  return result.ok ? result.code : null;
}

/**
 * Reads the artifact marker off a URL. Anything unrecognised falls back to
 * "link", because an unattributed claim is still a claim and guessing wrong on
 * an attribution row is cheaper than losing the student.
 */
export function readClaimSource(search: string): ClaimSource {
  const query = (search ?? "").replace(/^\?/, "");
  for (const pair of query.split("&")) {
    const [key, raw] = pair.split("=");
    if (key !== "s" || raw === undefined) continue;
    const candidate = URL_SOURCE_BY_TOKEN.get(decodeURIComponent(raw).toLowerCase());
    if (candidate) return candidate;
  }
  return "link";
}
